import * as React from 'react'
import { twMerge } from 'tailwind-merge'

import { Popover as PopoverPrimitive } from 'radix-ui'

import { POPOVER_WIDTH_MAP } from './constants'
import { XClose } from '@/assets/icons'

export type PopoverWidth = keyof typeof POPOVER_WIDTH_MAP

export const Popover = PopoverPrimitive.Root
export const PopoverTrigger = PopoverPrimitive.Trigger
export const PopoverClose = PopoverPrimitive.Close
export const PopoverAnchor = PopoverPrimitive.Anchor

export interface PopoverContentProps extends Omit<React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>, 'asChild'> {
  /** @default md (364px) */
  width?: PopoverWidth
  /** @default false (no close button) */
  showCloseButton?: boolean
  /** @default true (reserve right padding for the close button) */
  reserveCloseButtonSpace?: boolean
  /** @default true (show arrow) */
  showArrow?: boolean
  container?: HTMLElement | null
}

const styles = {
  content: twMerge(
    'z-[1000] rounded-lg bg-white relative',
    'shadow-[0_10px_38px_-10px_hsla(206,22%,7%,.35),0_10px_20px_-15px_hsla(206,22%,7%,.2)] p-5',
    'focus:outline-none',
    'will-change-[transform,opacity]',
    'data-[state=open]:data-[side=top]:animate-slideDownAndFade',
    'data-[state=open]:data-[side=right]:animate-slideLeftAndFade',
    'data-[state=open]:data-[side=bottom]:animate-slideUpAndFade',
    'data-[state=open]:data-[side=left]:animate-slideRightAndFade'
  ),
  arrow: 'fill-white',
  closeButton:
    'group absolute top-1 right-1 size-7 inline-flex items-center justify-center rounded-md text-brand-neutral-500 outline-none cursor-pointer focus-visible:ring-2 focus-visible:ring-brand-500',
  closeButtonInner: 'size-6 inline-flex items-center justify-center rounded-full group-hover:bg-brand-neutral-200 group-focus-visible:bg-brand-neutral-200',
  closeButtonIcon: 'size-4',
}

/**
 * Styled popover content wrapper built on Radix UI.
 *
 * <Popover>
 *   <PopoverTrigger>Open</PopoverTrigger>
 *   <PopoverContent width="md" showArrow>Content here</PopoverContent>
 * </Popover>
 */
export const PopoverContent = React.forwardRef<React.ElementRef<typeof PopoverPrimitive.Content>, React.PropsWithChildren<PopoverContentProps>>(
  (
    { width = 'md', showArrow = true, showCloseButton = false, reserveCloseButtonSpace = true, container, className, children, sideOffset = 5, ...props },
    forwardedRef
  ) => {
    return (
      <PopoverPrimitive.Portal container={container}>
        <PopoverPrimitive.Content
          {...props}
          ref={forwardedRef}
          data-ds-component="Popover"
          data-ds-width={width}
          sideOffset={sideOffset}
          // The close button is absolutely positioned (top-1 right-1, size-7) — so when
          // it's enabled we reserve right padding on the content so it doesn't visually
          // overlap wide first-line text.
          className={twMerge(
            styles.content,
            showCloseButton && reserveCloseButtonSpace && (width === 'auto' ? 'pr-8' : 'pr-10'),
            POPOVER_WIDTH_MAP[width],
            className
          )}
        >
          {children}
          {showCloseButton && (
            <PopoverClose asChild>
              <button type="button" className={styles.closeButton} aria-label="Close">
                <span className={styles.closeButtonInner}>
                  <XClose className={styles.closeButtonIcon} />
                </span>
              </button>
            </PopoverClose>
          )}
          {showArrow && <PopoverPrimitive.Arrow className={styles.arrow} width={16} height={8} />}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    )
  }
)

PopoverContent.displayName = 'PopoverContent'
